/**
 * Types TypeScript qui miment la forme JSON du `WorldState` Rust exposé
 * par le WASM via `WasmEngine::currentStateJson()`.
 *
 * Source de vérité : `crates/core/src/engine/mod.rs`. Si la struct Rust
 * évolue, mettre à jour ces types et ajouter un test côté Rust qui
 * sérialise un cas connu et confirme la forme attendue.
 */

export type AgeGroup = "children" | "youth" | "adults" | "seniors";
export type Csp =
  | "workers"
  | "executives"
  | "self_employed"
  | "unemployed"
  | "retired"
  | "students";
export type Health = "healthy" | "fragile" | "chronic" | "disabled";
export type Politics =
  "progressive" | "centrist" | "conservative" | "populist" | "abstainer";
export type RegimeType = "democracy" | "dictatorship";

export interface Cohort {
  id: number;
  age: AgeGroup;
  csp: Csp;
  health: Health;
  politics: Politics;
  count: number;
  annual_income: number;
  capital: number;
  savings: number;
  debt_ratio: number;
  satisfaction: number;
  trust_in_institutions: number;
  media_exposure: number;
  urbanization_ratio: number;
  education_index: number;
}

export interface Population {
  cohorts: Cohort[];
}

export type Sector =
  "agriculture" | "industry" | "services" | "digital" | "energy" | "finance";
export type TaxType =
  "income_tax" | "corporate_tax" | "vat" | "wealth_tax" | "customs" | "other";
export type ExpenseCategory =
  | "health"
  | "education"
  | "defense"
  | "social"
  | "investment"
  | "debt_service"
  | "other";

export interface Budget {
  revenues: Record<TaxType, number>;
  expenses: Record<ExpenseCategory, number>;
}

/** Énergie v1 — mini-état prix/mix/usure. Mirror de `EnergyState` (Rust).
 *  Les champs `independence`/`cost_index`/… sont des DÉRIVÉS recalculés
 *  chaque tour par le moteur. Optionnel pour compat avec les states
 *  antérieurs au 2026-07-02. */
export interface EnergyState {
  world_price: number;
  fossil_share: number;
  nuclear_share: number;
  renewable_share: number;
  fleet_wear: number;
  shield: number;
  independence: number;
  cost_index: number;
  carbon_intensity: number;
  poverty_rate: number;
  elec_margin: number;
}

export interface Economy {
  gdp: number;
  gdp_by_sector: Record<Sector, number>;
  inflation: number;
  public_debt: number;
  budget: Budget;
  money_supply: number;
  currency_strength: number;
  trade_balance: number;
  key_rate: number;
  unemployment_rate: number;
  /** Momentum d'investissement (EWMA) — alimente la croissance avec un lag. */
  investment_momentum?: number;
  /** Énergie v1 — absent des states antérieurs au 2026-07-02. */
  energy?: EnergyState;
}

export interface PoliticalProfile {
  economic: number;
  societal: number;
  international: number;
  authority: number;
}

/** J1 — cause de fin de mandat. */
export type MandateEndCause =
  | "term"
  | "motion_censure"
  | "faillite"
  | "coup_etat"
  /** S18 — en dictature, l'effondrement durable devient révolution de rue. */
  | "revolution"
  /** Europe S3 — mise sous tutelle (3 paliers UE critiques actifs). */
  | "mise_sous_tutelle_ue"
  /** Contrat de campagne — promesse trahie + pays effondré : le peuple
   *  chasse le parjure. */
  | "trahison_promesse";

/** K2 — niveau d'escalade politique. */
export type CrisisLevel = "none" | "rising" | "critical";

/** K1 — Acteur politique nommé (5 personae). */
export interface Actor {
  id: string;
  label: string;
  kind:
    | "union"
    | "lobby"
    | "media"
    | "opposition"
    | "ngo"
    | "institution"
    | "movement";
  /** Jauge de pertinence (0..5+). À 5.0, l'acteur passe known_to_player. */
  relevance: number;
  relation_to_player: number;
  known_to_player: boolean;
  narrative_on_reveal?: string;
  media_source?: string;
  /** L+2 — actor_impacts accumulés en latent, vidés à l'éveil. */
  pending_relation_delta?: number;
}

export interface RevealedCohortGroup {
  id: string;
  label: string;
  revealed_at_turn: number;
  current_satisfaction: number;
  population_share_pct: number;
}

/** Europe S3 — gravité d'une procédure (compte les critiques pour MiseSousTutelleUE). */
export type EuSeverity = "minor" | "major" | "critical";

/** Europe S1+ — trace d'une procédure européenne déclenchée. */
export interface EuropeanIntervention {
  id: string;
  triggered_at_turn: number;
  headline_text: string;
  source: string;
  /** Europe S3 — gravité copiée du def au moment du déclenchement. */
  severity?: EuSeverity;
}

/** Leçon de mécanisme apprise (vérité assistée — crate::mechanism). */
export interface LearnedLesson {
  option_id: string;
  decision_id: string;
  option_label: string;
  learned_at_turn: number;
  effects: string[];
  narrative: string;
}

export interface Polity {
  regime: RegimeType;
  profile: PoliticalProfile;
  repression_index: number;
  /** S18 — arc « Dérive autoritaire » actif (None = jamais déclenché). */
  authoritarian_arc?: {
    stage: "suspicion" | "tournant" | "verrouillage" | "installe";
    entered_at_turn: number;
    stage_entered_at: number;
  } | null;
  actors: Actor[];
  /** H3 — flag levé quand le mandat se termine (terme normal ou anticipé). */
  mandate_ended?: boolean;
  /** J1 — cause du terme. `null`/`undefined` tant que le mandat continue. */
  mandate_end_cause?: MandateEndCause | null;
  /** K2 — niveau d'escalade courant (None / Rising / Critical). */
  crisis_level?: CrisisLevel;
  /** Sprint Cohortes émergentes — groupes sociaux nommés ayant émergé. */
  revealed_cohort_groups?: RevealedCohortGroup[];
  /** Europe S1+ — procédures européennes déclenchées (append-only). */
  european_interventions?: EuropeanIntervention[];
  /**
   * Approbation mesurée (sondages) — distincte de la satisfaction
   * réelle des cohortes. Combine satisfaction + biais média
   * (Leclerc) + biais régime (dictature truque ×1.4+20) + bruit ±2.
   * Activée 2026-05-12. Avant cette date, le front affichait
   * directement `mean_satisfaction` sous le label « Approbation ».
   */
  approval_rate?: number;
  /** Leçons de mécanismes apprises (rappel UI sous les options). */
  learned_lessons?: LearnedLesson[];
}

export type ForeignArchetype =
  | "superpower"
  | "rival"
  | "ally"
  | "periphery"
  | "emerging"
  | "failing"
  | "neutral";

export type Specialty =
  | "energy"
  | "agriculture"
  | "tech"
  | "finance"
  | "manufacturing"
  | "military"
  | "culture";

export type Resource =
  "gas" | "oil" | "food" | "semiconductors" | "rare_metals";

export interface Dependency {
  resource: Resource;
  share: number;
}

export interface ForeignCountry {
  /** Le serde Rust sérialise CountryId(u8) comme un objet `[0]: u8`. */
  id: { "0": number };
  label: string;
  archetype: ForeignArchetype;
  specialties: Specialty[];
  economic_relation: number;
  political_relation: number;
  trade_balance: number;
  dependencies: Dependency[];
  known_to_player: boolean;
}

export type GlobalClimate = "calm" | "tense" | "cold_war" | "open_conflict";

export interface Geopolitics {
  countries: ForeignCountry[];
  global_climate: GlobalClimate;
}

export interface WorldState {
  turn: number;
  population: Population;
  economy: Economy;
  geopolitics: Geopolitics;
  polity: Polity;
}

export type EventUrgency =
  "fait_divers" | "incident" | "crise_locale" | "crise_majeure" | "cataclysme";

export interface GameEvent {
  id: string;
  urgency: EventUrgency;
  narrative: string;
  tags?: string[];
}

/** I3 — headline narrative émise par le moteur (voix des cohortes, etc.). */
export interface Headline {
  text: string;
  source: string;
}

export interface TurnResult {
  new_state: WorldState;
  events: GameEvent[];
  revelations: Revelation[];
  headlines: Headline[];
  /** R4c — impacts appliqués par événement (attribution « Ce tour-ci »). */
  event_impacts?: { narrative: string; sat_delta: number }[];
}

export interface Revelation {
  kind: "indicator" | "cohort" | "mechanism" | "actor";
  target_id: string;
  narrative: string;
}

// ---------------------------------------------------------------------------
// Decision (miroir partiel de la struct Rust — on ne garde que ce dont l'UI a
// besoin pour afficher l'offre et l'aperçu d'impact).
// ---------------------------------------------------------------------------

export type DecisionKind =
  "rupture" | "operational" | "event_driven" | "routine";

export interface ImpactLinearCurve {
  type: "linear";
  delta: number;
}
export interface ImpactAdditiveCurve {
  type: "additive";
  delta: number;
}
export interface ImpactThresholdCurve {
  type: "threshold";
  at: number;
  below: number;
  above: number;
}
export type ImpactCurve =
  ImpactLinearCurve | ImpactAdditiveCurve | ImpactThresholdCurve;

export interface Impact {
  property: string;
  curve: ImpactCurve;
}

export interface TargetByAge {
  type: "by_age";
  ages: AgeGroup[];
}
export interface TargetByCsp {
  type: "by_csp";
  csps: Csp[];
}
export interface TargetByHealth {
  type: "by_health";
  healths: Health[];
}
export interface TargetByPolitics {
  type: "by_politics";
  politics: Politics[];
}
export interface TargetIntersection {
  type: "intersection";
  selectors: TargetSelector[];
}
export interface TargetEconomy {
  type: "economy";
  component: string;
}
export interface TargetAll {
  type: "all";
}
export type TargetSelector =
  | TargetByAge
  | TargetByCsp
  | TargetByHealth
  | TargetByPolitics
  | TargetIntersection
  | TargetEconomy
  | TargetAll;

export interface Consequence {
  target: TargetSelector;
  impacts: Impact[];
}

export type OptionPosition = "primary" | "secondary";

export interface FiscalImpact {
  delta_revenue_billions?: number;
  delta_expense_billions?: number;
  revenue_category?: TaxType;
  expense_category?: ExpenseCategory;
}

export interface DecisionOption {
  id: string;
  label: string;
  position: OptionPosition;
  profile_shift_delta: PoliticalProfile;
  consequences: Consequence[];
  /** Phrase d'impact rédigée à la main. Si absent, le matcher narratif
   *  compose la phrase à partir des tropes (cf. engine/narratives.ts). */
  impact_summary?: string;
  /** Impact budgétaire direct en valeurs absolues (Md€). */
  fiscal_impact?: FiscalImpact;
  /** Effets différés propres à cette option (s'ajoutent à ceux de l'archétype). */
  delayed_effects?: unknown[];
}

export interface Decision {
  id: string;
  kind: DecisionKind;
  domain: string;
  intensity: number;
  kicker: string;
  prompt: string;
  context: string;
  description: string;
  profile_shift: PoliticalProfile;
  base_consequences?: Consequence[];
  delayed_effects: unknown[];
  options: DecisionOption[];
  hidden_consequences?: unknown[];
  reveal_hints?: unknown[];
  tags?: string[];
  /** L1 — entrées de glossaire pour les termes opaques au grand public. */
  glossary?: GlossEntry[];
  /** « Agir » — id d'indicateur si cette décision est un redressement réservé. */
  remediation_for?: string;
}

/** L1 — paire (terme, définition courte) pour démystifier le jargon. */
export interface GlossEntry {
  term: string;
  def: string;
}

/**
 * Agrégats dérivés côté TS — on ne les transporte pas depuis le WASM
 * pour rester frugal en bytes. Calculés à la volée depuis les cohortes.
 */
export interface DerivedAggregates {
  totalPopulation: number;
  meanSatisfaction: number;
  meanTrust: number;
  weightedIncome: number;
  executivesCount: number;
  workersCount: number;
  retiredCount: number;
}

export function deriveAggregates(state: WorldState): DerivedAggregates {
  const cohorts = state.population.cohorts;
  const total = cohorts.reduce((a, c) => a + c.count, 0);
  if (total === 0) {
    return {
      totalPopulation: 0,
      meanSatisfaction: 0,
      meanTrust: 0,
      weightedIncome: 0,
      executivesCount: 0,
      workersCount: 0,
      retiredCount: 0,
    };
  }
  const sat = cohorts.reduce((a, c) => a + c.satisfaction * c.count, 0) / total;
  const trust =
    cohorts.reduce((a, c) => a + c.trust_in_institutions * c.count, 0) / total;
  const income =
    cohorts.reduce((a, c) => a + c.annual_income * c.count, 0) / total;

  const byCsp = (csp: Csp) =>
    cohorts.filter((c) => c.csp === csp).reduce((a, c) => a + c.count, 0);

  return {
    totalPopulation: total,
    meanSatisfaction: sat,
    meanTrust: trust,
    weightedIncome: income,
    executivesCount: byCsp("executives"),
    workersCount: byCsp("workers"),
    retiredCount: byCsp("retired"),
  };
}
